import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { dashboardService, teamService } from "../services/api";
import { useDepartement } from "../context/DepartementContext";
import "./Dashboard.css";

/* ---------- helpers ---------- */
const toSeconds = (hms) => {
  if (!hms) return 0;
  const parts = hms.split(":").map(Number);
  return parts[0] * 3600 + parts[1] * 60 + (parts[2] || 0);
};

const toHoursDecimal = (hms) => (toSeconds(hms) / 3600).toFixed(2);

const formatHMS = (hms) => {
  if (!hms || hms === "00:00:00") return "0h 00m";
  const [h, m] = hms.split(":");
  return `${parseInt(h, 10)}h ${m}m`;
};

const getWeekRange = () => {
  const today = new Date();
  const day = today.getDay();
  const start = new Date(today);
  start.setDate(today.getDate() - day);
  return {
    start: start.toISOString().slice(0, 10),
    end: today.toISOString().slice(0, 10),
  };
};

const getMonthRange = () => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  return {
    start: start.toISOString().slice(0, 10),
    end: today.toISOString().slice(0, 10),
  };
};

const getYesterday = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

const CHART_COLORS = [
  "#3498db",
  "#2ecc71",
  "#e67e22",
  "#9b59b6",
  "#e74c3c",
  "#1abc9c",
  "#f39c12",
  "#34495e",
  "#16a085",
  "#8e44ad",
  "#d35400",
  "#27ae60",
  "#2980b9",
  "#c0392b",
  "#7f8c8d",
];

const TEAM_COLORS = [
  ["#3498dbcc", "#3498db"],
  ["#e74c3ccc", "#e74c3c"],
  ["#2ecc71cc", "#2ecc71"],
  ["#f39c12cc", "#f39c12"],
  ["#9b59b6cc", "#9b59b6"],
  ["#1abc9ccc", "#1abc9c"],
  ["#e67e22cc", "#e67e22"],
  ["#34495ecc", "#34495e"],
  ["#e91e63cc", "#e91e63"],
  ["#00bcdacc", "#00bcda"],
];

/* Report 1 categories */
const R1_CATEGORIES = [
  {
    key: "normal",
    label: "Normal Hours",
    color: "#27ae60",
    colorAlpha: "rgba(39,174,96,0.12)",
    icon: "fa-clock",
  },
  {
    key: "overtime",
    label: "Overtime Hours",
    color: "#e74c3c",
    colorAlpha: "rgba(231,76,60,0.12)",
    icon: "fa-hourglass-half",
  },
  {
    key: "idleNorm",
    label: "Idle Time",
    color: "#e67e22",
    colorAlpha: "rgba(230,126,34,0.12)",
    icon: "fa-pause",
  },
  {
    key: "idleOv",
    label: "Idle Overtime",
    color: "#9b59b6",
    colorAlpha: "rgba(155,89,182,0.12)",
    icon: "fa-moon",
  },
  {
    key: "total",
    label: "Total Hours",
    color: "#3498db",
    colorAlpha: "rgba(52,152,219,0.12)",
    icon: "fa-chart-bar",
  },
];

/* ---------- trip table helpers ---------- */
const ROWS_PER_PAGE = 10;

function otClass(overtime) {
  if (!overtime || overtime.startsWith("-") || overtime === "00:00:00")
    return "none";
  const parts = overtime.split(":").map(Number);
  const totalMin = parts[0] * 60 + (parts[1] || 0);
  if (totalMin < 60) return "low";
  return "high";
}

function formatOt(overtime) {
  if (!overtime || overtime.startsWith("-") || overtime === "00:00:00")
    return "0h 00m";
  const parts = overtime.split(":").map(Number);
  return `${parts[0]}h ${String(parts[1] || 0).padStart(2, "0")}m`;
}

/* ---------- component ---------- */
function DashboardPage() {
  const titleSlot = document.getElementById("page-title-slot");
  const { selectedDepartement } = useDepartement();

  /* ---- main filter state ---- */
  const [filterType, setFilterType] = useState("day");
  const [startDate, setStartDate] = useState(getYesterday());
  const [endDate, setEndDate] = useState(getYesterday());
  const [teamId, setTeamId] = useState("");

  /* ---- data state ---- */
  const [teams, setTeams] = useState([]);
  const [poiTeamData, setPoiTeamData] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [hoursByPoi, setHoursByPoi] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /* ---- Hours by POI card filters ---- */
  const [poiCardTeam, setPoiCardTeam]             = useState('');
  const [poiCardPoiFilters, setPoiCardPoiFilters] = useState([]); // array of selected POI names
  const [poiDropdownOpen, setPoiDropdownOpen]     = useState(false);
  const [poiCardLoading, setPoiCardLoading]       = useState(false);
  const poiDropdownRef                            = useRef(null);

  /* ---- trip table state ---- */
  const [trips, setTrips] = useState([]);
  const [tripsLoading, setTripsLoading] = useState(false);
  const [tableSearch, setTableSearch] = useState("");
  const [tablePage, setTablePage] = useState(1);
  const [tripDateFrom, setTripDateFrom] = useState(getYesterday());
  const [tripDateTo, setTripDateTo] = useState(getYesterday());
  const [tripTeam, setTripTeam] = useState("");

  /* ---- Report 1 state ---- */
  const [r1DateFrom, setR1DateFrom] = useState(getYesterday());
  const [r1DateTo, setR1DateTo] = useState(getYesterday());
  const [r1Team, setR1Team] = useState("");
  const [r1Category, setR1Category] = useState("normal");
  const [poiMatrix, setPoiMatrix] = useState(null);
  const [r1Loading, setR1Loading] = useState(false);

  /* ---- Report 2 state ---- */
  const [r2DateFrom, setR2DateFrom] = useState(getYesterday());
  const [r2DateTo, setR2DateTo] = useState(getYesterday());
  const [r2Team, setR2Team] = useState("");
  const [earlyLate, setEarlyLate] = useState(null);
  const [r2Loading, setR2Loading] = useState(false);

  /* ---- chart refs ---- */
  const poiChartRef = useRef(null);
  const teamChartRef = useRef(null);
  const poiChartInstance = useRef(null);
  const teamChartInstance = useRef(null);

  /* ---- trip table helpers ---- */
  const filteredTrips = trips.filter(
    (t) =>
      (t.vehicleName || "").toLowerCase().includes(tableSearch.toLowerCase()) ||
      (t.poiName || "").toLowerCase().includes(tableSearch.toLowerCase()),
  );
  const totalPages = Math.max(
    1,
    Math.ceil(filteredTrips.length / ROWS_PER_PAGE),
  );
  const pageTrips = filteredTrips.slice(
    (tablePage - 1) * ROWS_PER_PAGE,
    tablePage * ROWS_PER_PAGE,
  );


  function handleSearch(val) {
    setTableSearch(val);
    setTablePage(1);
  }

  /* ---- load teams ---- */
  useEffect(() => {
    const params = selectedDepartement
      ? { departementId: selectedDepartement.id }
      : {};
    teamService
      .getAll(params)
      .then((res) => {
        setTeams(Array.isArray(res.data?.result) ? res.data.result : []);
        setTeamId("");
      })
      .catch(() => setTeams([]));
  }, [selectedDepartement]);

  /* ---- fetch main dashboard (KPI + Hours by POI + Team chart) ---- */
  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);

    const params = {};
    if (teamId) params.teamId = teamId;
    if (selectedDepartement?.id) params.departementId = selectedDepartement.id;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    Promise.allSettled([
      dashboardService.getKPIs(params),
      dashboardService.getPoiTeamDashboard(params),
    ])
      .then(([kpiRes, teamRes]) => {
        if (kpiRes.status === "fulfilled") setKpis(kpiRes.value.data);
        if (teamRes.status === "fulfilled") setPoiTeamData(teamRes.value.data);
        const anyFailed = [kpiRes, teamRes].some(
          (r) => r.status === "rejected",
        );
        if (anyFailed) setError("Some data could not be loaded.");
      })
      .finally(() => setLoading(false));
  }, [teamId, selectedDepartement, startDate, endDate]);

  /* ---- fetch Hours by POI (card-level filters) ---- */
  const fetchHoursByPoi = useCallback(() => {
    setPoiCardLoading(true);
    const params = {};
    if (poiCardTeam) params.teamId = poiCardTeam;
    if (selectedDepartement?.id) params.departementId = selectedDepartement.id;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    dashboardService
      .getHoursByPOI(params)
      .then((res) => setHoursByPoi(Array.isArray(res.data) ? res.data : []))
      .catch(() => setHoursByPoi([]))
      .finally(() => setPoiCardLoading(false));
  }, [poiCardTeam, selectedDepartement, startDate, endDate]);

  /* ---- fetch Trip Details ---- */
  const fetchTripDetails = useCallback(() => {
    setTripsLoading(true);
    const params = {};
    if (tripTeam) params.teamId = tripTeam;
    if (tripDateFrom) params.startDate = tripDateFrom;
    if (tripDateTo) params.endDate = tripDateTo;
    if (selectedDepartement?.id) params.departementId = selectedDepartement.id;

    dashboardService
      .getTripDetails(params)
      .then((res) => {
        setTrips(Array.isArray(res.data) ? res.data : []);
        setTablePage(1);
      })
      .catch(() => setTrips([]))
      .finally(() => setTripsLoading(false));
  }, [tripTeam, tripDateFrom, tripDateTo, selectedDepartement]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchHoursByPoi();
  }, [fetchHoursByPoi]);

  useEffect(() => {
    fetchTripDetails();
  }, [fetchTripDetails]);

  /* ---- fetch Report 1: POI Matrix ---- */
  const fetchPoiMatrix = useCallback(() => {
    setR1Loading(true);
    const params = {};
    if (r1Team) params.teamId = r1Team;
    if (r1DateFrom) params.startDate = r1DateFrom;
    if (r1DateTo) params.endDate = r1DateTo;
    if (selectedDepartement?.id) params.departementId = selectedDepartement.id;

    dashboardService
      .getPoiMatrix(params)
      .then((res) => setPoiMatrix(res.data))
      .catch(() => setPoiMatrix(null))
      .finally(() => setR1Loading(false));
  }, [r1Team, r1DateFrom, r1DateTo, selectedDepartement]);

  useEffect(() => {
    fetchPoiMatrix();
  }, [fetchPoiMatrix]);

  /* ---- fetch Report 2: Early/Late ---- */
  const fetchEarlyLate = useCallback(() => {
    setR2Loading(true);
    const params = {};
    if (r2Team) params.teamId = r2Team;
    if (r2DateFrom) params.startDate = r2DateFrom;
    if (r2DateTo) params.endDate = r2DateTo;
    if (selectedDepartement?.id) params.departementId = selectedDepartement.id;

    dashboardService
      .getEarlyLate(params)
      .then((res) => setEarlyLate(Array.isArray(res.data) ? res.data : []))
      .catch(() => setEarlyLate([]))
      .finally(() => setR2Loading(false));
  }, [r2Team, r2DateFrom, r2DateTo, selectedDepartement]);

  useEffect(() => {
    fetchEarlyLate();
  }, [fetchEarlyLate]);

  /* ---- close POI dropdown on outside click ---- */
  useEffect(() => {
    const handleOutside = (e) => {
      if (poiDropdownRef.current && !poiDropdownRef.current.contains(e.target)) {
        setPoiDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  /* ---- filtered Hours by POI (client-side multi-POI filter) ---- */
  const filteredHoursByPoi = poiCardPoiFilters.length > 0
    ? (hoursByPoi || []).filter((p) => poiCardPoiFilters.includes(p.poiName))
    : hoursByPoi;

  /* ---- build / update Hours by POI chart (stacked) ---- */
  useEffect(() => {
    if (!filteredHoursByPoi || !poiChartRef.current) return;
    const Chart = window.Chart;
    if (!Chart) return;

    if (poiChartInstance.current) poiChartInstance.current.destroy();

    if (filteredHoursByPoi.length === 0) return;

    const labels = filteredHoursByPoi.map((p) => p.poiName);
    const normalData = filteredHoursByPoi.map((p) =>
      toHoursDecimal(p.normalHours),
    );
    const overtimeData = filteredHoursByPoi.map((p) =>
      toHoursDecimal(p.overtimeHours),
    );
    const idleNormData = filteredHoursByPoi.map((p) =>
      toHoursDecimal(p.idleNormal),
    );
    const idleOvData = filteredHoursByPoi.map((p) =>
      toHoursDecimal(p.idleOvertime),
    );

    poiChartInstance.current = new Chart(poiChartRef.current, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label: "Normal Hours",
            data: normalData,
            backgroundColor: "#3498dbcc",
            borderColor: "#3498db",
            borderWidth: 1,
            borderRadius: 4,
            borderSkipped: false,
          },
          {
            label: "Overtime Hours",
            data: overtimeData,
            backgroundColor: "#e74c3ccc",
            borderColor: "#e74c3c",
            borderWidth: 1,
            borderRadius: 4,
            borderSkipped: false,
          },
          {
            label: "Idling Time",
            data: idleNormData,
            backgroundColor: "#e67e22cc",
            borderColor: "#e67e22",
            borderWidth: 1,
            borderRadius: 4,
            borderSkipped: false,
          },
          {
            label: "Idling (Overtime)",
            data: idleOvData,
            backgroundColor: "#9b59b6cc",
            borderColor: "#9b59b6",
            borderWidth: 1,
            borderRadius: 4,
            borderSkipped: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: "top",
            labels: { font: { size: 11 }, boxWidth: 12 },
          },
          tooltip: {
            mode: "index",
            intersect: false,
            backgroundColor: "rgba(30,40,60,0.92)",
            padding: 12,
            titleFont: { size: 13, weight: "600" },
            bodyFont: { size: 12 },
            callbacks: {
              label: (ctx) => `  ${ctx.dataset.label}: ${ctx.raw}h`,
              footer: (items) => {
                const total = items.reduce(
                  (s, i) => s + parseFloat(i.raw || 0),
                  0,
                );
                return `  Total: ${total.toFixed(2)}h`;
              },
            },
            footerFont: { size: 12, weight: "600" },
            footerColor: "#facc15",
            footerMarginTop: 6,
          },
        },
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
            ticks: {
              maxRotation: 40,
              minRotation: 30,
              font: { size: 11 },
              color: "#6b7280",
            },
          },
          y: {
            stacked: true,
            beginAtZero: true,
            grid: { color: "rgba(0,0,0,0.05)" },
            ticks: {
              callback: (v) => `${v}h`,
              font: { size: 11 },
              color: "#6b7280",
            },
            title: {
              display: true,
              text: "Hours",
              color: "#9ca3af",
              font: { size: 11 },
            },
          },
        },
      },
    });

    return () => {
      if (poiChartInstance.current) poiChartInstance.current.destroy();
    };
  }, [hoursByPoi, poiCardPoiFilters, poiCardLoading]);

  /* ---- build / update Team chart ---- */
  useEffect(() => {
    if (!poiTeamData || !teamChartRef.current) return;
    const Chart = window.Chart;
    if (!Chart) return;

    if (teamChartInstance.current) teamChartInstance.current.destroy();

    const labels = poiTeamData.total_hour_team.map((t) => t.team_name);
    const values = poiTeamData.total_hour_team.map((t) =>
      (toSeconds(t.total_time) / 3600).toFixed(2),
    );

    teamChartInstance.current = new Chart(teamChartRef.current, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label: "Hours (h)",
            data: values,
            backgroundColor: labels.map(
              (_, i) => TEAM_COLORS[i % TEAM_COLORS.length][0],
            ),
            borderColor: labels.map(
              (_, i) => TEAM_COLORS[i % TEAM_COLORS.length][1],
            ),
            borderWidth: 1,
            borderRadius: 6,
            borderSkipped: false,
          },
        ],
      },
      options: {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "rgba(30,40,60,0.92)",
            padding: 12,
            titleFont: { size: 13, weight: "600" },
            bodyFont: { size: 12 },
            callbacks: { label: (ctx) => `  ${ctx.raw} hours` },
          },
        },
        scales: {
          y: {
            grid: { display: false },
            ticks: { font: { size: 11 }, color: "#6b7280" },
          },
          x: {
            beginAtZero: true,
            grid: { color: "rgba(0,0,0,0.05)" },
            ticks: {
              callback: (v) => `${v}h`,
              font: { size: 11 },
              color: "#6b7280",
            },
            title: {
              display: true,
              text: "Hours",
              color: "#9ca3af",
              font: { size: 11 },
            },
          },
        },
      },
    });

    return () => {
      if (teamChartInstance.current) teamChartInstance.current.destroy();
    };
  }, [poiTeamData]);

  /* ---- sync report dates when global filter changes ---- */
  useEffect(() => {
    setTripDateFrom(startDate);
    setTripDateTo(endDate);
    setR1DateFrom(startDate);
    setR1DateTo(endDate);
    setR2DateFrom(startDate);
    setR2DateTo(endDate);
  }, [startDate, endDate]);

  const handleFilterTypeChange = (type) => {
    setFilterType(type);
    if (type === "day") {
      const y = getYesterday();
      setStartDate(y);
      setEndDate(y);
    } else if (type === "week") {
      const r = getWeekRange();
      setStartDate(r.start);
      setEndDate(r.end);
    } else if (type === "month") {
      const r = getMonthRange();
      setStartDate(r.start);
      setEndDate(r.end);
    }
  };

  /* ---- derived KPI values ---- */
  const formatTotalH = (hms) => {
    if (!hms) return "—";
    const [h, m] = hms.split(":");
    return `${parseInt(h, 10)}h ${m}m`;
  };

  /* ---- Report 1 helpers ---- */

  /** Returns the cell value in seconds for a team row and POI index, based on active category */
  const r1CellSec = (team, poiIdx) => {
    if (!team) return 0;
    const n = toSeconds(team.normalHours[poiIdx] || "00:00:00");
    const ov = toSeconds(team.overtimeHours[poiIdx] || "00:00:00");
    const iN = toSeconds(team.idleNormal[poiIdx] || "00:00:00");
    const iO = toSeconds(team.idleOvertime[poiIdx] || "00:00:00");
    switch (r1Category) {
      case "normal":
        return n;
      case "overtime":
        return ov;
      case "idleNorm":
        return iN;
      case "idleOv":
        return iO;
      case "total":
        return n + ov + iN + iO;
      default:
        return 0;
    }
  };

  const secToHm = (sec) => {
    const hh = Math.floor(sec / 3600);
    const mm = Math.floor((sec % 3600) / 60);
    return `${hh}h ${String(mm).padStart(2, "0")}m`;
  };

  const r1ColTotal = (poiIdx) => {
    if (!poiMatrix) return "0h 00m";
    const teamsToSum = r1Team
      ? poiMatrix.teams.filter((t) => t.teamId === r1Team)
      : poiMatrix.teams;
    return secToHm(teamsToSum.reduce((s, t) => s + r1CellSec(t, poiIdx), 0));
  };

  const r1RowTotal = (team) => {
    if (!poiMatrix || !team) return "0h 00m";
    return secToHm(
      poiMatrix.poiLabels.reduce((s, _, i) => s + r1CellSec(team, i), 0),
    );
  };

  /* ---- Report 2 helpers ---- */
  const r2Rows = earlyLate
    ? r2Team
      ? earlyLate.filter((r) => r.teamId === r2Team)
      : earlyLate
    : [];

  const r2TeamOptions = earlyLate
    ? [...new Map(earlyLate.map((r) => [r.teamId, r])).values()]
    : [];

  return (
    <>
      {titleSlot &&
        createPortal(
          <h1 className="dashboard-page-title">
            {selectedDepartement
              ? `Dashboard — ${selectedDepartement.name}`
              : "Dashboard"}
          </h1>,
          titleSlot,
        )}

      <div className="df-container">
        {/* ---- Main Filters ---- */}
        <div className="df-filters-card">
          <div className="df-filters-row">
            <div className="df-filter-group">
              <label>Period</label>
              <div className="df-preset-btns">
                {[
                  { key: "day", label: "Yesterday" },
                  { key: "week", label: "This week" },
                  { key: "month", label: "This month" },
                  { key: "custom", label: "Custom" },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    className={`df-preset-btn${filterType === key ? " active" : ""}`}
                    onClick={() => handleFilterTypeChange(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {filterType === "custom" && (
              <>
                <div className="df-filter-group">
                  <label>From</label>
                  <input
                    type="date"
                    className="df-input"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="df-filter-group">
                  <label>To</label>
                  <input
                    type="date"
                    className="df-input"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </>
            )}

            <div className="df-filter-group">
              <label>Team</label>
              <select
                className="df-input"
                value={teamId}
                onChange={(e) => setTeamId(e.target.value)}
              >
                <option value="">All teams</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="df-filter-group df-filter-action">
              <button
                className="df-apply-btn"
                onClick={fetchData}
                disabled={loading}
              >
                <i className="fas fa-sync-alt"></i>
                {loading ? " Loading..." : " Apply"}
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="df-error">
            <i className="fas fa-exclamation-circle"></i> {error}
          </div>
        )}

        {loading && !kpis && (
          <div className="df-loading-row">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="df-kpi-skeleton" />
            ))}
          </div>
        )}

        {/* ---- KPI Cards (dynamic) ---- */}
        <div className="df-kpi-grid">
          <div className="df-kpi-card blue">
            <div className="df-kpi-info">
              <h3>Normal Hours</h3>
              <div className="df-kpi-value">
                {kpis ? formatTotalH(kpis.normalHours) : "—"}
              </div>
              <small style={{ color: "#9ca3af" }}>
                Total for selected period
              </small>
            </div>
            <div className="df-kpi-icon">
              <i className="fas fa-clock"></i>
            </div>
          </div>
          <div className="df-kpi-card green">
            <div className="df-kpi-info">
              <h3>Overtime Hours</h3>
              <div className="df-kpi-value">
                {kpis ? formatTotalH(kpis.overtimeHours) : "—"}
              </div>
              <small style={{ color: "#9ca3af" }}>
                Total for selected period
              </small>
            </div>
            <div className="df-kpi-icon">
              <i className="fas fa-hourglass-half"></i>
            </div>
          </div>
          <div className="df-kpi-card orange">
            <div className="df-kpi-info">
              <h3>Late Arrivals</h3>
              <div className="df-kpi-value">
                {kpis != null ? kpis.lateCount : "—"}
              </div>
              <small style={{ color: "#9ca3af" }}>Total occurrences</small>
            </div>
            <div className="df-kpi-icon">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
          </div>
          <div className="df-kpi-card purple">
            <div className="df-kpi-info">
              <h3>Early Departures</h3>
              <div className="df-kpi-value">
                {kpis != null ? kpis.earlyCount : "—"}
              </div>
              <small style={{ color: "#9ca3af" }}>Total occurrences</small>
            </div>
            <div className="df-kpi-icon">
              <i className="fas fa-sign-out-alt"></i>
            </div>
          </div>
        </div>

        {/* ---- Charts ---- */}
        <div className="df-charts-grid">
          <div className="df-chart-card">
            <div className="df-chart-header">
              <h3>
                <span className="df-chart-title-icon blue">
                  <i className="fas fa-chart-bar"></i>
                </span>
                Hours by POI
              </h3>
            </div>
            <div
              className="df-report-filters"
              style={{ padding: "0 0 10px 0", gap: "10px", flexWrap: "wrap" }}
            >
              <div className="df-report-filter-group">
                <label>Team</label>
                <select
                  className="df-input"
                  value={poiCardTeam}
                  onChange={(e) => setPoiCardTeam(e.target.value)}
                >
                  <option value="">All Teams</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="df-report-filter-group">
                <label>POI</label>
                <div className="df-poi-dropdown" ref={poiDropdownRef}>
                  <button
                    className="df-poi-trigger"
                    onClick={() => setPoiDropdownOpen((o) => !o)}
                  >
                    <span>
                      {poiCardPoiFilters.length === 0
                        ? 'All POIs'
                        : poiCardPoiFilters.length <= 2
                          ? poiCardPoiFilters.join(' · ')
                          : `${poiCardPoiFilters.length} POIs selected`}
                    </span>
                    <i className={`fas fa-chevron-${poiDropdownOpen ? 'up' : 'down'} df-poi-chevron`}></i>
                  </button>
                  {poiDropdownOpen && (
                    <div className="df-poi-panel">
                      <div className="df-poi-controls">
                        <button className="df-poi-ctrl-btn" onClick={() => setPoiCardPoiFilters((hoursByPoi || []).map((p) => p.poiName))}>
                          Select All
                        </button>
                        <button className="df-poi-ctrl-btn" onClick={() => setPoiCardPoiFilters([])}>
                          Clear All
                        </button>
                      </div>
                      <div className="df-poi-checklist">
                        {(hoursByPoi || []).map((p) => {
                          const checked = poiCardPoiFilters.includes(p.poiName);
                          return (
                            <label key={p.poiName} className="df-poi-check-item">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => setPoiCardPoiFilters((prev) =>
                                  checked ? prev.filter((n) => n !== p.poiName) : [...prev, p.poiName]
                                )}
                              />
                              <span>{p.poiName}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="df-report-filter-group">
                <button className="df-apply-btn" onClick={() => { setPoiCardTeam(''); setPoiCardPoiFilters([]); }}>
                  <i className="fas fa-times"></i> Reset
                </button>
              </div>
            </div>
            <div className="df-chart-wrapper">
              {poiCardLoading ? (
                <p className="df-no-data">Loading...</p>
              ) : filteredHoursByPoi && filteredHoursByPoi.length === 0 ? (
                <p className="df-no-data">No data</p>
              ) : (
                <canvas ref={poiChartRef} />
              )}
            </div>
          </div>

          <div className="df-chart-card">
            <div className="df-chart-header">
              <h3>
                <span className="df-chart-title-icon green">
                  <i className="fas fa-users"></i>
                </span>
                Hours by Team
              </h3>
            </div>
            <div className="df-chart-wrapper">
              {loading ? (
                <p className="df-no-data">Loading...</p>
              ) : poiTeamData && poiTeamData.total_hour_team.length === 0 ? (
                <p className="df-no-data">No data</p>
              ) : (
                <canvas ref={teamChartRef} />
              )}
            </div>
          </div>
        </div>

        {/* ---- Trip Details ---- */}
        <div className="df-table-card">
          <div className="df-table-header">
            <h3>
              <i className="fas fa-table"></i> Trip Details
            </h3>
            <div className="df-search-box">
              <input
                type="text"
                placeholder="Search vehicle or POI..."
                value={tableSearch}
                onChange={(e) => handleSearch(e.target.value)}
              />
              <button className="df-btn-filter">
                <i className="fas fa-search"></i>
              </button>
            </div>
          </div>

          <div className="df-report-filters">
            <div className="df-report-filter-group">
              <label>Date From</label>
              <input
                type="date"
                className="df-input"
                value={tripDateFrom}
                onChange={(e) => setTripDateFrom(e.target.value)}
              />
            </div>
            <div className="df-report-filter-group">
              <label>Date To</label>
              <input
                type="date"
                className="df-input"
                value={tripDateTo}
                onChange={(e) => setTripDateTo(e.target.value)}
              />
            </div>
            <div className="df-report-filter-group">
              <label>Filter Team</label>
              <select
                className="df-input"
                value={tripTeam}
                onChange={(e) => setTripTeam(e.target.value)}
              >
                <option value="">All Teams</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="df-report-filter-group">
              <button
                className="df-apply-btn"
                onClick={fetchTripDetails}
                disabled={tripsLoading}
              >
                <i className="fas fa-sync-alt"></i>{" "}
                {tripsLoading ? "Loading..." : "Apply"}
              </button>
            </div>
          </div>

          <div className="df-table-scroll">
            <table className="df-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Vehicle</th>
                  <th>Team</th>
                  <th>POI (Site)</th>
                  <th>Entry Time</th>
                  <th>Exit Time</th>
                  <th>Duration</th>
                  <th>Overtime</th>
                </tr>
              </thead>
              <tbody>
                {tripsLoading ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', color: '#9ca3af' }}>Loading...</td></tr>
                ) : pageTrips.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', color: '#9ca3af' }}>No data</td></tr>
                ) : pageTrips.map((trip, idx) => (
                  <tr key={`${trip.date}-${trip.vehicleName}-${trip.poiName}-${idx}`}>
                    <td>{trip.date || '—'}</td>
                    <td><i className="fas fa-shuttle-van df-cell-icon"></i>{trip.vehicleName || '—'}</td>
                    <td><i className="fas df-cell-icon"></i>{trip.name || '—'}</td>
                    <td><i className="fas fa-map-marker-alt df-cell-poi-icon"></i>{trip.poiName || '—'}</td>
                    <td><span className="df-time-badge">{trip.entryTime || '—'}</span></td>
                    <td><span className="df-time-badge">{trip.exitTime || '—'}</span></td>
                    <td>{trip.duration || '—'}</td>
                    <td>
                      <span className={`df-overtime-badge ${otClass(trip.overtime)}`}>{formatOt(trip.overtime)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="df-pagination">
            <button
              onClick={() => setTablePage((p) => p - 1)}
              disabled={tablePage === 1}
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                className={tablePage === n ? "active" : ""}
                onClick={() => setTablePage(n)}
              >
                {n}
              </button>
            ))}
            <button
              onClick={() => setTablePage((p) => p + 1)}
              disabled={tablePage === totalPages}
            >
              Next
            </button>
          </div>
        </div>

        {/* ---- Report 1 — POI Hours Matrix ---- */}
        {(() => {
          const activeCat = R1_CATEGORIES.find((c) => c.key === r1Category);
          const displayTeams = poiMatrix
            ? r1Team
              ? poiMatrix.teams.filter((t) => t.teamId === r1Team)
              : poiMatrix.teams
            : [];
          const poiLabels = poiMatrix ? poiMatrix.poiLabels : [];
          const filteredTeamNameR1 = r1Team
            ? poiMatrix?.teams.find((t) => t.teamId === r1Team)?.teamName ||
              "Team"
            : null;

          return (
            <div
              className="df-table-card df-r1-card"
              style={{
                "--r1-color": activeCat.color,
                "--r1-color-bg": activeCat.colorAlpha,
              }}
            >
              <div className="df-table-header">
                <h3>
                  <i className="fas fa-th"></i> Report 1 &middot; POI Hours
                  Matrix
                </h3>
                <div className="df-r1-header-right">
                  {filteredTeamNameR1 && (
                    <span className="df-r2-team-chip">
                      <i className="fas fa-users"></i> {filteredTeamNameR1}
                    </span>
                  )}
                  <span
                    className="df-r1-active-badge"
                    style={{
                      background: activeCat.colorAlpha,
                      color: activeCat.color,
                      border: `1px solid ${activeCat.color}44`,
                    }}
                  >
                    <i className={`fas ${activeCat.icon}`}></i>{" "}
                    {activeCat.label}
                  </span>
                </div>
              </div>

              <div className="df-report-filters df-r1-filters">
                <div className="df-report-filter-group">
                  <label>Date From</label>
                  <input
                    type="date"
                    className="df-input"
                    value={r1DateFrom}
                    onChange={(e) => setR1DateFrom(e.target.value)}
                  />
                </div>
                <div className="df-report-filter-group">
                  <label>Date To</label>
                  <input
                    type="date"
                    className="df-input"
                    value={r1DateTo}
                    onChange={(e) => setR1DateTo(e.target.value)}
                  />
                </div>
                <div className="df-report-filter-group">
                  <label>Filter Team</label>
                  <select
                    className="df-input"
                    value={r1Team}
                    onChange={(e) => setR1Team(e.target.value)}
                  >
                    <option value="">All Teams</option>
                    {poiMatrix &&
                      poiMatrix.teams.map((t) => (
                        <option key={t.teamId} value={t.teamId}>
                          {t.teamName}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="df-report-filter-group">
                  <button
                    className="df-apply-btn"
                    onClick={fetchPoiMatrix}
                    disabled={r1Loading}
                  >
                    <i className="fas fa-sync-alt"></i>{" "}
                    {r1Loading ? "Loading..." : "Apply"}
                  </button>
                </div>
              </div>

              <div className="df-report-cat-tabs">
                {R1_CATEGORIES.map((cat) => (
                  <button
                    key={cat.key}
                    className={`df-report-cat-btn${r1Category === cat.key ? " active" : ""}`}
                    style={
                      r1Category === cat.key
                        ? {
                            background: cat.color,
                            borderColor: cat.color,
                            color: "#fff",
                            boxShadow: `0 4px 14px ${cat.colorAlpha.replace("0.12", "0.40")}`,
                          }
                        : { borderColor: `${cat.color}55`, color: cat.color }
                    }
                    onClick={() => setR1Category(cat.key)}
                  >
                    <i className={`fas ${cat.icon}`}></i> {cat.label}
                  </button>
                ))}
              </div>

              {r1Loading ? (
                <p className="df-no-data">Loading...</p>
              ) : !poiMatrix || poiLabels.length === 0 ? (
                <p className="df-no-data">No data for selected period</p>
              ) : (
                <div className="df-table-scroll">
                  <table className="df-table df-report-matrix">
                    <thead>
                      <tr>
                        <th>Team</th>
                        {poiLabels.map((p) => (
                          <th key={p}>{p}</th>
                        ))}
                        <th
                          className="df-report-total-col"
                          style={{
                            color: activeCat.color,
                            background: activeCat.colorAlpha,
                          }}
                        >
                          <i
                            className={`fas ${activeCat.icon}`}
                            style={{ marginRight: 5 }}
                          ></i>
                          {activeCat.label}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayTeams.map((team) => (
                        <tr key={team.teamId}>
                          <td className="df-report-team-cell">
                            {team.teamName}
                          </td>
                          {poiLabels.map((_, i) => (
                            <td key={i}>{secToHm(r1CellSec(team, i))}</td>
                          ))}
                          <td
                            className="df-report-total-col"
                            style={{
                              color: activeCat.color,
                              background: activeCat.colorAlpha,
                              fontWeight: 700,
                            }}
                          >
                            {r1RowTotal(team)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="df-report-total-row">
                        <td>Total</td>
                        {poiLabels.map((_, i) => (
                          <td key={i}>{r1ColTotal(i)}</td>
                        ))}
                        <td
                          className="df-report-total-col"
                          style={{
                            color: activeCat.color,
                            background: activeCat.colorAlpha,
                          }}
                        >
                          —
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          );
        })()}

        {/* ---- Report 2 — Late Arrival & Early Departure ---- */}
        {(() => {
          const totalLate = r2Rows.reduce((s, r) => s + (r.nbLate || 0), 0);
          const totalEarly = r2Rows.reduce((s, r) => s + (r.nbEarly || 0), 0);
          const filteredTeamNameR2 = r2Team
            ? r2TeamOptions.find((t) => t.teamId === r2Team)?.team || "Team"
            : null;

          return (
            <div className="df-table-card df-r2-card">
              <div className="df-table-header">
                <h3>
                  <i className="fas fa-clock"></i> Report 2 &middot; Late
                  Arrival &amp; Early Departure
                </h3>
                {filteredTeamNameR2 && (
                  <span className="df-r2-team-chip">
                    <i className="fas fa-users"></i> {filteredTeamNameR2}
                  </span>
                )}
              </div>

              <div className="df-report-filters">
                <div className="df-report-filter-group">
                  <label>Date From</label>
                  <input
                    type="date"
                    className="df-input"
                    value={r2DateFrom}
                    onChange={(e) => setR2DateFrom(e.target.value)}
                  />
                </div>
                <div className="df-report-filter-group">
                  <label>Date To</label>
                  <input
                    type="date"
                    className="df-input"
                    value={r2DateTo}
                    onChange={(e) => setR2DateTo(e.target.value)}
                  />
                </div>
                <div className="df-report-filter-group">
                  <label>Filter Team</label>
                  <select
                    className="df-input"
                    value={r2Team}
                    onChange={(e) => setR2Team(e.target.value)}
                  >
                    <option value="">All Teams</option>
                    {r2TeamOptions.map((r) => (
                      <option key={r.teamId} value={r.teamId}>
                        {r.team}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="df-report-filter-group">
                  <button
                    className="df-apply-btn"
                    onClick={fetchEarlyLate}
                    disabled={r2Loading}
                  >
                    <i className="fas fa-sync-alt"></i>{" "}
                    {r2Loading ? "Loading..." : "Apply"}
                  </button>
                </div>
              </div>

              {!r2Loading && r2Rows.length > 0 && (
                <div className="df-r2-summary">
                  <div className="df-r2-stat df-r2-stat--late">
                    <i className="fas fa-exclamation-triangle df-r2-stat-icon"></i>
                    <div className="df-r2-stat-body">
                      <span className="df-r2-stat-val">{totalLate}</span>
                      <span className="df-r2-stat-lbl">Late Arrivals</span>
                    </div>
                  </div>
                  <div className="df-r2-stat df-r2-stat--early">
                    <i className="fas fa-sign-out-alt df-r2-stat-icon"></i>
                    <div className="df-r2-stat-body">
                      <span className="df-r2-stat-val">{totalEarly}</span>
                      <span className="df-r2-stat-lbl">Early Departures</span>
                    </div>
                  </div>
                </div>
              )}

              {r2Loading ? (
                <p className="df-no-data">Loading...</p>
              ) : (
                <div className="df-table-scroll">
                  <table className="df-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Team Name</th>
                        <th>
                          <i
                            className="fas fa-exclamation-triangle"
                            style={{ color: "#dc2626", marginRight: 5 }}
                          ></i>
                          Late Arrivals
                        </th>
                        <th>
                          <i
                            className="fas fa-sign-out-alt"
                            style={{ color: "#d97706", marginRight: 5 }}
                          ></i>
                          Early Departures
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {r2Rows.length === 0 ? (
                        <tr>
                          <td
                            colSpan={4}
                            style={{ textAlign: "center", color: "#9ca3af" }}
                          >
                            No data
                          </td>
                        </tr>
                      ) : (
                        r2Rows.map((row, idx) => (
                          <tr key={`${row.teamId}-${row.dateReport}-${idx}`}>
                            <td>{row.dateReport || "—"}</td>
                            <td className="df-report-team-cell">{row.team}</td>
                            <td>
                              <span
                                className={`df-badge-late${row.nbLate === 0 ? " zero" : ""}`}
                              >
                                {row.nbLate}
                              </span>
                            </td>
                            <td>
                              <span
                                className={`df-badge-early${row.nbEarly === 0 ? " zero" : ""}`}
                              >
                                {row.nbEarly}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    <tfoot>
                      <tr className="df-report-total-row">
                        <td colSpan={2}>Total</td>
                        <td>
                          <span className="df-badge-late">{totalLate}</span>
                        </td>
                        <td>
                          <span className="df-badge-early">{totalEarly}</span>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </>
  );
}

export default DashboardPage;
